<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useToast } from '@/composables/useToast'
import DisplayFieldsConfig from '@/components/campaign/DisplayFieldsConfig.vue'

// 👉 Types
interface VForm {
  validate(): Promise<{ valid: boolean }>
}

// Define page meta for CASL permissions
definePage({
  meta: {
    action: 'create',
    subject: 'Campaign',
  },
})

// 👉 Composables
const { t } = useI18n()
const { showToast } = useToast()
const router = useRouter()

// 👉 Stepper configuration
const numberedSteps = [
  {
    title: t('campaign.basicInfo'),
    subtitle: t('campaign.basicInfoDescription'),
  },
  {
    title: t('campaign.relations'),
    subtitle: t('campaign.relationsDescription'),
  },
  {
    title: t('campaign.displayFields'),
    subtitle: t('campaign.displayFieldsDescription'),
  },
  {
    title: t('campaign.phoneSystem'),
    subtitle: t('campaign.phoneSystemDescription'),
  },
]

const currentStep = ref(0)
const isCurrentStepValid = ref(true)
const saving = ref(false)

// 👉 Form refs
const refBasicForm = ref<VForm>()
const refRelationsForm = ref<VForm>()
const refDisplayFieldsForm = ref<VForm>()
const refPhoneSystemForm = ref<VForm>()

// 👉 Step 1: Basic Info
const basicForm = ref({
  name: '',
  detail: '',
  startDate: '',
  endDate: '',
  maxTotalWrapup: null as number | null,
  maxWrapupPerGroup: {} as Record<string, number>,
  wrapupTemplateId: null as number | null,
})

// 👉 Step 2: Relations
const relationsForm = ref({
  teamIds: [] as number[],
  callListIds: [] as number[],
  externalAppIds: [] as number[],
  dncGroupIds: [] as number[],
})

// 👉 Step 3: Display Fields
const displayFields = ref({
  dataTable: [] as any[],
  callInfo: [] as any[],
  callLog: [] as any[],
})

// 👉 Step 4: Phone System Config
const phoneSystemConfig = ref({
  system: '',
  config: {} as Record<string, any>,
})

// 👉 Data fetching
const { data: wrapupTemplatesData, execute: loadWrapupTemplates } = useApi<any>(createUrl('/wrapup-template/active'))
const { data: teamsData, execute: loadTeams } = useApi<any>(createUrl('/teams/active'))
const { data: callListsData, execute: loadCallLists } = useApi<any>(createUrl('/call-lists/active'))
const { data: externalAppsData, execute: loadExternalApps } = useApi<any>(createUrl('/external-apps/active'))
const { data: dncGroupsData, execute: loadDncGroups } = useApi<any>(createUrl('/dnc/groups/active'))
const { data: wrapupGroupStatusesData, execute: loadWrapupGroupStatuses } = useApi<any>(createUrl('/wrapup-group-status/active'))
const { data: fieldMappingData, execute: loadFieldMapping } = useApi<any>(createUrl('/call-lists/field-mapping'))

// 👉 Computed properties
const wrapupTemplates = computed(() => wrapupTemplatesData.value || []) // Wrapup templates /active endpoint returns array directly
const teams = computed(() => teamsData.value || []) // Teams /active endpoint returns array directly
const callLists = computed(() => callListsData.value || []) // Call lists /active endpoint returns array directly
const externalApps = computed(() => externalAppsData.value || []) // External apps /all endpoint returns array directly
const dncGroups = computed(() => dncGroupsData.value || []) // DNC groups endpoint returns array directly
const wrapupGroupStatuses = computed(() => wrapupGroupStatusesData.value || []) // Wrapup group statuses /active endpoint returns array directly
const availableFields = computed(() => fieldMappingData.value?.fields || []) // Field mapping from calllist import

// 👉 Phone system options
const phoneSystemOptions = computed(() => [
  { title: t('campaign.cxone'), value: 'cxone' },
  { title: t('campaign.genesys'), value: 'genesys' },
  { title: t('campaign.3cx'), value: '3cx' },
  { title: t('campaign.aspect'), value: 'aspect' },
  { title: t('campaign.asterisk'), value: 'asterisk' },
  { title: t('campaign.other'), value: 'other' },
])

// 👉 Phone system config JSON string
const phoneSystemConfigJson = computed({
  get: () => {
    if (typeof phoneSystemConfig.value.config === 'object' && phoneSystemConfig.value.config !== null)
      return JSON.stringify(phoneSystemConfig.value.config, null, 2)

    return ''
  },
  set: (value: string) => {
    try {
      if (value.trim())
        phoneSystemConfig.value.config = JSON.parse(value)
      else
        phoneSystemConfig.value.config = {}
    }
    catch (error) {
      // Keep the string value if JSON is invalid
      phoneSystemConfig.value.config = value
    }
  },
})

// 👉 Get config example based on phone system
const getConfigExample = computed(() => {
  const examples: Record<string, string> = {
    'asterisk': `{
  "host": "asterisk.example.com",
  "port": 5038,
  "username": "admin",
  "password": "secret",
  "context": "from-internal",
  "extension": "1000",
  "callerIdName": "Campaign Name",
  "callerIdNumber": "1234567890",
  "timeout": 30,
  "variables": {
    "CAMPAIGN_ID": "123",
    "CUSTOM_FIELD": "value"
  }
}`,
    'cxone': `{
  "apiUrl": "https://api.cxone.com",
  "apiKey": "your-api-key",
  "campaignId": "12345"
}`,
    'genesys': `{
  "apiUrl": "https://api.genesys.com",
  "clientId": "your-client-id",
  "clientSecret": "your-client-secret"
}`,
    '3cx': `{
  "host": "3cx.example.com",
  "port": 5001,
  "username": "admin",
  "password": "secret"
}`,
    'aspect': `{
  "apiUrl": "https://aspect.example.com",
  "apiKey": "your-api-key"
}`,
    'other': `{
  "customField1": "value1",
  "customField2": "value2"
}`,
  }

  return examples[phoneSystemConfig.value.system] || ''
})

// 👉 Update phone system config
const updatePhoneSystemConfig = (value: string) => {
  try {
    if (value.trim())
      phoneSystemConfig.value.config = JSON.parse(value)
    else
      phoneSystemConfig.value.config = {}
  }
  catch (error) {
    // Keep the string value if JSON is invalid
    phoneSystemConfig.value.config = value
  }
}

// 👉 Save campaign
const saveCampaign = async () => {
  try {
    saving.value = true

    const campaignData = {
      ...basicForm.value,
      startDate: basicForm.value.startDate ? new Date(basicForm.value.startDate).toISOString() : null,
      endDate: basicForm.value.endDate ? new Date(basicForm.value.endDate).toISOString() : null,
      displayFields: displayFields.value,
      phoneSystemConfig: phoneSystemConfig.value.system ? phoneSystemConfig.value : null,
    }


    // Create campaign
    const response = await $api('/campaigns', {
      method: 'POST',
      body: campaignData,
    })

    const campaignId = response.id

    // Map relations if provided
    if (relationsForm.value.teamIds.length > 0) {
      await $api(`/campaigns/${campaignId}/map-teams`, {
        method: 'PATCH',
        body: { teamIds: relationsForm.value.teamIds },
      })
    }

    if (relationsForm.value.callListIds.length > 0) {
      await $api(`/campaigns/${campaignId}/map-call-lists`, {
        method: 'PATCH',
        body: { callListIds: relationsForm.value.callListIds },
      })
    }

    if (relationsForm.value.externalAppIds.length > 0) {
      await $api(`/campaigns/${campaignId}/map-external-apps`, {
        method: 'PATCH',
        body: { externalAppIds: relationsForm.value.externalAppIds },
      })
    }

    if (relationsForm.value.dncGroupIds.length > 0) {
      await $api(`/campaigns/${campaignId}/dnc-groups`, {
        method: 'PATCH',
        body: { dncGroupIds: relationsForm.value.dncGroupIds },
      })
    }

    showToast({
      type: 'success',
      message: t('campaign.campaignCreatedSuccessfully'),
    })

    router.push('/campaign/list')
  }
  catch (error) {
    console.error('Error creating campaign:', error)
    showToast({
      type: 'error',
      message: t('campaign.errorCreatingCampaign'),
    })
  }
  finally {
    saving.value = false
  }
}

// 👉 Validation functions
const validateBasicForm = () => {
  refBasicForm.value?.validate().then((valid: { valid: boolean }) => {
    if (valid.valid) {
      currentStep.value++
      isCurrentStepValid.value = true
    }
    else {
      isCurrentStepValid.value = false
    }
  })
}

const validateRelationsForm = () => {
  refRelationsForm.value?.validate().then((valid: { valid: boolean }) => {
    if (valid.valid) {
      currentStep.value++
      isCurrentStepValid.value = true
    }
    else {
      isCurrentStepValid.value = false
    }
  })
}

const validateDisplayFieldsForm = () => {
  refDisplayFieldsForm.value?.validate().then((valid: { valid: boolean }) => {
    if (valid.valid) {
      currentStep.value++
      isCurrentStepValid.value = true
    }
    else {
      isCurrentStepValid.value = false
    }
  })
}

const validatePhoneSystemForm = () => {
  refPhoneSystemForm.value?.validate().then((valid: { valid: boolean }) => {
    if (valid.valid) {
      isCurrentStepValid.value = true
      saveCampaign()
    }
    else {
      isCurrentStepValid.value = false
    }
  })
}

// 👉 Add wrapup limit for group status
const addWrapupLimit = (groupStatusId: number) => {
  basicForm.value.maxWrapupPerGroup[groupStatusId.toString()] = 0
}

// 👉 Remove wrapup limit for group status
const removeWrapupLimit = (groupStatusId: number) => {
  delete basicForm.value.maxWrapupPerGroup[groupStatusId.toString()]
}

// 👉 Load data on mount
onMounted(() => {
  loadWrapupTemplates()
  loadTeams()
  loadCallLists()
  loadExternalApps()
  loadDncGroups()
  loadWrapupGroupStatuses()
  loadFieldMapping()
})
</script>

<template>
  <section>
    <!-- 👉 Page Header -->
    <div class="mb-6">
      <h1 class="text-h4 font-weight-bold mb-2">
        {{ t('campaign.createCampaign') }}
      </h1>
      <p class="text-body-1 text-medium-emphasis">
        {{ t('campaign.createCampaignDescription') }}
      </p>
    </div>

    <VCard>
      <VCardText>
        <!-- 👉 Stepper -->
        <AppStepper
          v-model:current-step="currentStep"
          :items="numberedSteps"
          :is-active-step-valid="isCurrentStepValid"
        />
      </VCardText>

      <VDivider />

      <VCardText>
        <!-- 👉 Stepper Content -->
        <VWindow
          v-model="currentStep"
          class="disable-tab-transition"
        >
          <!-- 👉 Step 1: Basic Info -->
          <VWindowItem>
            <VForm
              ref="refBasicForm"
              @submit.prevent="validateBasicForm"
            >
              <VRow>
                <VCol cols="12">
                  <h6 class="text-h6 font-weight-medium">
                    {{ t('campaign.basicInfo') }}
                  </h6>
                  <p class="mb-0">
                    {{ t('campaign.basicInfoDescription') }}
                  </p>
                </VCol>

                <!-- Campaign Name -->
                <VCol
                  cols="12"
                  md="6"
                >
                  <AppTextField
                    v-model="basicForm.name"
                    :label="t('campaign.campaignName')"
                    :placeholder="t('campaign.enterCampaignName')"
                    :rules="[requiredValidator]"
                  />
                </VCol>

                <!-- Campaign Detail -->
                <VCol
                  cols="12"
                  md="6"
                >
                  <AppTextField
                    v-model="basicForm.detail"
                    :label="t('campaign.campaignDetail')"
                    :placeholder="t('campaign.enterCampaignDetail')"
                  />
                </VCol>

                <!-- Start Date -->
                <VCol
                  cols="12"
                  md="6"
                >
                  <AppDateTimePicker
                    v-model="basicForm.startDate"
                    :label="t('campaign.startDate')"
                    :placeholder="t('campaign.selectStartDate')"
                    type="date"
                  />
                </VCol>

                <!-- End Date -->
                <VCol
                  cols="12"
                  md="6"
                >
                  <AppDateTimePicker
                    v-model="basicForm.endDate"
                    :label="t('campaign.endDate')"
                    :placeholder="t('campaign.selectEndDate')"
                    type="date"
                  />
                </VCol>

                <!-- Max Total Wrapup -->
                <VCol
                  cols="12"
                  md="6"
                >
                  <AppTextField
                    v-model.number="basicForm.maxTotalWrapup"
                    :label="t('campaign.maxTotalWrapup')"
                    :placeholder="t('campaign.enterMaxTotalWrapup')"
                    type="number"
                    min="0"
                  />
                </VCol>

                <!-- Wrapup Template -->
                <VCol
                  cols="12"
                  md="6"
                >
                  <AppSelect
                    v-model="basicForm.wrapupTemplateId"
                    :label="t('campaign.wrapupTemplate')"
                    :placeholder="t('campaign.selectWrapupTemplate')"
                    :items="wrapupTemplates"
                    item-title="name"
                    item-value="id"
                    clearable
                  />
                </VCol>

                <!-- Max Wrapup Per Group Status -->
                <VCol cols="12">
                  <h6 class="text-h6 font-weight-medium mb-4">
                    {{ t('campaign.maxWrapupPerGroup') }}
                  </h6>

                  <div class="d-flex flex-wrap gap-4 mb-4">
                    <VBtn
                      v-for="groupStatus in wrapupGroupStatuses"
                      :key="groupStatus.id"
                      :color="basicForm.maxWrapupPerGroup[groupStatus.id.toString()] !== undefined ? 'primary' : 'secondary'"
                      variant="outlined"
                      size="small"
                      @click="basicForm.maxWrapupPerGroup[groupStatus.id.toString()] !== undefined ? removeWrapupLimit(groupStatus.id) : addWrapupLimit(groupStatus.id)"
                    >
                      {{ groupStatus.name }}
                    </VBtn>
                  </div>

                  <div
                    v-for="(limit, groupStatusId) in basicForm.maxWrapupPerGroup"
                    :key="groupStatusId"
                    class="d-flex align-center gap-4 mb-2"
                  >
                    <VTextField
                      v-model.number="basicForm.maxWrapupPerGroup[groupStatusId]"
                      :label="wrapupGroupStatuses.find((gs: any) => gs.id === parseInt(groupStatusId))?.name"
                      type="number"
                      min="0"
                      style="max-width: 200px;"
                    />
                    <VBtn
                      icon
                      variant="text"
                      color="error"
                      size="small"
                      @click="removeWrapupLimit(parseInt(groupStatusId))"
                    >
                      <VIcon icon="tabler-x" />
                    </VBtn>
                  </div>
                </VCol>

                <VCol cols="12">
                  <div class="d-flex flex-wrap gap-4 justify-sm-space-between justify-center mt-8">
                    <VBtn
                      color="secondary"
                      variant="tonal"
                      disabled
                    >
                      <VIcon
                        icon="tabler-arrow-left"
                        start
                        class="flip-in-rtl"
                      />
                      {{ t('common.previous') }}
                    </VBtn>

                    <VBtn type="submit">
                      {{ t('common.next') }}
                      <VIcon
                        icon="tabler-arrow-right"
                        end
                        class="flip-in-rtl"
                      />
                    </VBtn>
                  </div>
                </VCol>
              </VRow>
            </VForm>
          </VWindowItem>

          <!-- 👉 Step 2: Relations -->
          <VWindowItem>
            <VForm
              ref="refRelationsForm"
              @submit.prevent="validateRelationsForm"
            >
              <VRow>
                <VCol cols="12">
                  <h6 class="text-h6 font-weight-medium">
                    {{ t('campaign.relations') }}
                  </h6>
                  <p class="mb-0">
                    {{ t('campaign.relationsDescription') }}
                  </p>
                </VCol>

                <!-- Teams -->
                <VCol cols="12">
                  <AppSelect
                    v-model="relationsForm.teamIds"
                    :label="t('campaign.teams')"
                    :placeholder="t('campaign.selectTeams')"
                    :items="teams"
                    item-title="name"
                    item-value="id"
                    multiple
                    chips
                    closable-chips
                  />
                </VCol>

                <!-- Call Lists -->
                <VCol cols="12">
                  <AppSelect
                    v-model="relationsForm.callListIds"
                    :label="t('campaign.callLists')"
                    :placeholder="t('campaign.selectCallLists')"
                    :items="callLists"
                    item-title="listName"
                    item-value="id"
                    multiple
                    chips
                    closable-chips
                  />
                </VCol>

                <!-- External Apps -->
                <VCol cols="12">
                  <AppSelect
                    v-model="relationsForm.externalAppIds"
                    :label="t('campaign.externalApps')"
                    :placeholder="t('campaign.selectExternalApps')"
                    :items="externalApps"
                    item-title="name"
                    item-value="id"
                    multiple
                    chips
                    closable-chips
                  />
                </VCol>

                <!-- DNC Groups -->
                <VCol cols="12">
                  <AppSelect
                    v-model="relationsForm.dncGroupIds"
                    :label="t('campaign.dncGroups')"
                    :placeholder="t('campaign.selectDncGroups')"
                    :items="dncGroups"
                    item-title="name"
                    item-value="id"
                    multiple
                    chips
                    closable-chips
                  />
                </VCol>

                <VCol cols="12">
                  <div class="d-flex flex-wrap gap-4 justify-sm-space-between justify-center mt-8">
                    <VBtn
                      color="secondary"
                      variant="tonal"
                      @click="currentStep--"
                    >
                      <VIcon
                        icon="tabler-arrow-left"
                        start
                        class="flip-in-rtl"
                      />
                      {{ t('common.previous') }}
                    </VBtn>

                    <VBtn type="submit">
                      {{ t('common.next') }}
                      <VIcon
                        icon="tabler-arrow-right"
                        end
                        class="flip-in-rtl"
                      />
                    </VBtn>
                  </div>
                </VCol>
              </VRow>
            </VForm>
          </VWindowItem>

          <!-- 👉 Step 3: Display Fields -->
          <VWindowItem>
            <VForm
              ref="refDisplayFieldsForm"
              @submit.prevent="validateDisplayFieldsForm"
            >
              <VRow>
                <VCol cols="12">
                  <h6 class="text-h6 font-weight-medium">
                    {{ t('campaign.displayFields') }}
                  </h6>
                  <p class="mb-0">
                    {{ t('campaign.displayFieldsDescription') }}
                  </p>
                </VCol>

                <VCol cols="12">
                  <DisplayFieldsConfig
                    v-model="displayFields"
                    :available-fields="availableFields"
                  />
                </VCol>

                <VCol cols="12">
                  <div class="d-flex flex-wrap gap-4 justify-sm-space-between justify-center mt-8">
                    <VBtn
                      color="secondary"
                      variant="tonal"
                      @click="currentStep--"
                    >
                      <VIcon
                        icon="tabler-arrow-left"
                        start
                        class="flip-in-rtl"
                      />
                      {{ t('common.previous') }}
                    </VBtn>

                    <VBtn type="submit">
                      {{ t('common.next') }}
                      <VIcon
                        icon="tabler-arrow-right"
                        end
                        class="flip-in-rtl"
                      />
                    </VBtn>
                  </div>
                </VCol>
              </VRow>
            </VForm>
          </VWindowItem>

          <!-- 👉 Step 4: Phone System Config -->
          <VWindowItem>
            <VForm
              ref="refPhoneSystemForm"
              @submit.prevent="validatePhoneSystemForm"
            >
              <VRow>
                <VCol cols="12">
                  <h6 class="text-h6 font-weight-medium">
                    {{ t('campaign.phoneSystemConfig') }}
                  </h6>
                  <p class="mb-0">
                    {{ t('campaign.phoneSystemConfigDescription') }}
                  </p>
                </VCol>

                <!-- Phone System -->
                <VCol
                  cols="12"
                  md="6"
                >
                  <AppSelect
                    v-model="phoneSystemConfig.system"
                    :label="t('campaign.phoneSystem')"
                    :placeholder="t('campaign.selectPhoneSystem')"
                    :items="phoneSystemOptions"
                    clearable
                  />
                </VCol>

                <!-- System Config (JSON) -->
                <VCol
                  v-if="phoneSystemConfig.system"
                  cols="12"
                >
                  <VTextarea
                    v-model="phoneSystemConfigJson"
                    :label="t('campaign.systemConfig')"
                    :placeholder="t('campaign.enterSystemConfig')"
                    rows="10"
                    :hint="t('campaign.enterPhoneSystemConfig')"
                    @update:model-value="updatePhoneSystemConfig"
                  />

                  <!-- Config Example -->
                  <VAlert
                    v-if="getConfigExample"
                    type="info"
                    variant="tonal"
                    class="mt-4"
                  >
                    <VAlertTitle>{{ t('campaign.configExample') || 'Configuration Example' }}</VAlertTitle>
                    <pre class="text-caption">{{ getConfigExample }}</pre>
                  </VAlert>
                </VCol>

                <VCol cols="12">
                  <div class="d-flex flex-wrap gap-4 justify-sm-space-between justify-center mt-8">
                    <VBtn
                      color="secondary"
                      variant="tonal"
                      @click="currentStep--"
                    >
                      <VIcon
                        icon="tabler-arrow-left"
                        start
                        class="flip-in-rtl"
                      />
                      {{ t('common.previous') }}
                    </VBtn>

                    <VBtn
                      color="success"
                      type="submit"
                      :loading="saving"
                    >
                      {{ t('campaign.createCampaign') }}
                    </VBtn>
                  </div>
                </VCol>
              </VRow>
            </VForm>
          </VWindowItem>
        </VWindow>
      </VCardText>
    </VCard>
  </section>
</template>
